import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, ShieldCheck, ArrowLeft, CheckCircle2, XCircle, CircleDashed } from 'lucide-react';
import { api } from '../api.js';
import { openStream, onWireState, type WireState } from '../stream.js';
import { Screen, Section, Card, Badge, Btn, Facts, Sha, EmptyState, Rows, ago, minutes } from '../ui/kit.js';
import { reporter } from '../pwa/report.js';

interface Candidate {
  runId: number;
  sha: string;
  version: string | null;
  title: string;
  status: string;
  conclusion: string | null;
  startedAt: string | null;
  durationMin: number | null;
  verified: boolean;
  shardsGreen: number;
  shardsTotal: number;
}
interface ReleasesData {
  at: string | null;
  candidates: Candidate[];
  fleetSha: string | null;
  fleetVersion: string | null;
  stagingSha: string | null;
  stagingVersion: string | null;
  ceremonyUrl: string;
  hqUrl: string;
}

const SHOW = 8;

// Opens instantly from the daemon's cache; the wire chip tells the truth
// about the live connection; a candidate opens into its own detail. The
// promotion ceremony itself renders inside Ops (see CeremonySheet), behind
// the Console owner's own sign-in — the door does not change.
export function Releases() {
  const navigate = useNavigate();
  const [data, setData] = useState<ReleasesData | null>(null);
  const [wire, setWire] = useState<WireState>('live');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const load = () => api.get<ReleasesData>('/api/releases').then(setData).catch(reporter('the releases'));
    void load();
    const stream = openStream('/api/events', { releases: () => void load() });
    const offWire = onWireState(setWire);
    return () => {
      stream.close();
      offWire();
    };
  }, []);

  if (!data) {
    return (
      <Screen title="Releases">
        <EmptyState title="Reading the release state…" />
      </Screen>
    );
  }

  /* V4 hotfix: "promotable" requires the candidate to be NEWER than the
     fleet — an older-and-verified one is a demotion, not a promotion,
     and the Console rejects it with a confusing message when tapped.
     Candidates arrive newest-first from GitHub. If the fleet SHA is in
     the list, only candidates ABOVE its position count; if the fleet
     is older than any listed candidate (fleet SHA absent), any verified
     candidate is newer. */
  const fleetIdx = data.fleetSha
    ? data.candidates.findIndex((c) => c.sha === data.fleetSha || (data.fleetSha!.startsWith(c.sha.slice(0, 8))))
    : -1;
  const newerThanFleet = fleetIdx >= 0 ? data.candidates.slice(0, fleetIdx) : data.candidates;
  const promotable = newerThanFleet.find((c) => c.verified && c.sha !== data.fleetSha);
  const visible = showAll ? data.candidates : data.candidates.slice(0, SHOW);

  return (
    <Screen
      title="Releases"
      sub="Candidates ride staging; a person promotes. Nothing here can touch a hospital."
      actions={<WireChip wire={wire} at={data.at} />}
    >
      <Facts
        items={[
          {
            label: 'Fleet runs',
            value: data.fleetVersion ?? (data.fleetSha ? data.fleetSha.slice(0, 8) : 'unreachable'),
            mono: true,
          },
          {
            label: 'Staging serves',
            value: data.stagingVersion ?? (data.stagingSha ? data.stagingSha.slice(0, 8) : 'unreachable'),
            mono: true,
          },
        ]}
      />

      {promotable ? (
        <Section title="Promotable">
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <ShieldCheck size={20} color="var(--ops-ok)" />
              <div style={{ minWidth: 0 }}>
                <div className="ops-row__title" data-testid="promotable-title">
                  {promotable.version ? `Version ${promotable.version}` : promotable.title || 'Verified candidate'}
                </div>
                <div className="ops-row__sub">
                  <Sha value={promotable.sha} /> · verified on staging
                  {data.fleetVersion ? ` · replaces ${data.fleetVersion}` : ''}
                </div>
              </div>
            </div>
            <Btn kind="primary" big onClick={() => navigate(`/releases/${promotable.runId}/promote?sha=${promotable.sha}`)}>
              Start the promotion ceremony
            </Btn>
            <p className="ops-hint" style={{ textAlign: 'center' }}>
              The same ceremony, every gate intact. You sign in as the Console owner inside it.
            </p>
          </Card>
        </Section>
      ) : null}

      <Section title="Candidates">
        {data.candidates.length === 0 ? (
          <EmptyState title="No candidates yet." />
        ) : (
          <>
            {visible.map((c) => (
              <Card key={c.runId} onTap={() => navigate(`/releases/${c.runId}`)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="ops-row__title">
                      {c.version ? `Version ${c.version}` : c.title || c.sha.slice(0, 8)}
                    </div>
                    <div className="ops-row__sub">
                      <Sha value={c.sha} /> · {c.startedAt ? `${ago(c.startedAt)} ago` : ''}
                      {c.shardsTotal ? ` · ${c.shardsGreen}/${c.shardsTotal} shards` : ''}
                      {c.sha === data.fleetSha ? ' · on the fleet' : ''}
                      {c.sha === data.stagingSha ? ' · on staging' : ''}
                    </div>
                  </div>
                  <CandidateBadge c={c} />
                </div>
              </Card>
            ))}
            {!showAll && data.candidates.length > SHOW ? (
              <button type="button" className="ops-more" onClick={() => setShowAll(true)} data-testid="candidates-more">
                Show {data.candidates.length - SHOW} more
              </button>
            ) : null}
          </>
        )}
      </Section>

      <Section title="Elsewhere">
        <Rows>
          <button type="button" className="ops-row" onClick={() => window.open(data.hqUrl, '_blank')}>
            <div className="ops-row__main">
              <div className="ops-row__title">HQ · Releases</div>
              <div className="ops-row__sub">Fleet, per-country lag, hospital by hospital</div>
            </div>
            <div className="ops-row__end">
              <ExternalLink size={16} />
            </div>
          </button>
          <button type="button" className="ops-row" onClick={() => window.open(data.ceremonyUrl, '_blank')}>
            <div className="ops-row__main">
              <div className="ops-row__title">Console · Releases</div>
              <div className="ops-row__sub">The same candidates and ceremony, in the Console</div>
            </div>
            <div className="ops-row__end">
              <ExternalLink size={16} />
            </div>
          </button>
        </Rows>
      </Section>
    </Screen>
  );
}

function WireChip(props: { wire: WireState; at: string | null }) {
  const label =
    props.wire === 'live' ? 'live' : props.wire === 'reconnecting' ? 'reconnecting' : 'offline';
  const tone = props.wire === 'live' ? 'ok' : props.wire === 'reconnecting' ? 'wait' : 'bad';
  return (
    <span title={props.at ? `data as of ${new Date(props.at).toLocaleTimeString()}` : undefined}>
      <Badge tone={tone as 'ok' | 'wait' | 'bad'} pulse={props.wire === 'live'}>
        {label}
      </Badge>
    </span>
  );
}

function CandidateBadge({ c }: { c: Candidate }) {
  if (c.status !== 'completed')
    return (
      <Badge tone="run" pulse>
        building
      </Badge>
    );
  if (c.verified) return <Badge tone="ok">verified</Badge>;
  if (c.conclusion === 'success') return <Badge tone="wait">green</Badge>;
  if (c.conclusion === 'cancelled') return <Badge tone="wait">superseded</Badge>;
  return <Badge tone="bad">{c.conclusion}</Badge>;
}

// ── Candidate detail ────────────────────────────────────────────────────────
interface DetailJob {
  name: string;
  status: string;
  conclusion: string | null;
  startedAt: string | null;
  completedAt: string | null;
  url: string | null;
}

export function CandidateDetail() {
  const { runId = '' } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<{
    candidate: Candidate | null;
    jobs: DetailJob[];
    onFleet: boolean;
    onStaging: boolean;
    opsJob: { id: string; title: string } | null;
  } | null>(null);

  useEffect(() => {
    void api.get<NonNullable<typeof detail>>(`/api/releases/candidates/${runId}`).then(setDetail).catch(reporter('the releases'));
  }, [runId]);

  if (!detail) {
    return (
      <Screen title="Candidate">
        <EmptyState title="Reading the candidate…" />
      </Screen>
    );
  }
  const c = detail.candidate;
  const shards = detail.jobs.filter((j) => /^Playwright shard/.test(j.name));
  const others = detail.jobs.filter((j) => !/^Playwright shard/.test(j.name));

  return (
    <Screen
      eyebrow="Releases"
      title={c?.version ? `Version ${c.version}` : c?.title || `Run ${runId}`}
      sub={
        c ? (
          <>
            <Sha value={c.sha} />
            {c.startedAt ? ` · built ${ago(c.startedAt)} ago` : ''}
            {c.durationMin ? ` in ${c.durationMin} min` : ''}
          </>
        ) : undefined
      }
      actions={
        <Btn kind="ghost" onClick={() => navigate('/releases')}>
          <ArrowLeft size={16} /> Back
        </Btn>
      }
    >
      {c?.verified && !detail.onFleet ? (
        <div style={{ marginBottom: 14 }}>
          <Btn kind="primary" big onClick={() => navigate(`/releases/${runId}/promote?sha=${c.sha}`)}>
            Start the promotion ceremony
          </Btn>
        </div>
      ) : null}
      <Facts
        items={[
          { label: 'State', value: c ? (c.verified ? 'Verified on staging' : c.status !== 'completed' ? 'Building' : (c.conclusion ?? '—')) : '—' },
          { label: 'Suite', value: c?.shardsTotal ? `${c.shardsGreen}/${c.shardsTotal} shards green` : '—' },
          { label: 'On staging', value: detail.onStaging ? 'yes' : 'no' },
          { label: 'On the fleet', value: detail.onFleet ? 'yes, hospitals run this' : 'not yet' },
        ]}
      />

      {detail.opsJob ? (
        <Section title="Born from">
          <Rows>
            <button type="button" className="ops-row" onClick={() => navigate(`/jobs/${detail.opsJob!.id}`)}>
              <div className="ops-row__main">
                <div className="ops-row__title">{detail.opsJob.title}</div>
                <div className="ops-row__sub">The Ops job whose merge became this candidate</div>
              </div>
            </button>
          </Rows>
        </Section>
      ) : null}

      <Section title="The build" aside={others.length ? `${others.length} lanes` : undefined}>
        <div className="ops-card">
          <div className="ops-ci">
            {others.map((j) => (
              <JobRow key={j.name} j={j} />
            ))}
          </div>
        </div>
      </Section>

      {shards.length ? (
        <Section title="Shards" aside={`${shards.filter((j) => j.conclusion === 'success').length}/${shards.length} green`}>
          <div className="ops-card">
            <div className="ops-ci">
              {shards.map((j) => (
                <JobRow key={j.name} j={j} />
              ))}
            </div>
          </div>
        </Section>
      ) : null}
    </Screen>
  );
}

function JobRow({ j }: { j: DetailJob }) {
  return (
    <button
      type="button"
      className="ops-ci__row"
      style={{ width: '100%', textAlign: 'left' }}
      onClick={() => j.url && window.open(j.url, '_blank')}
    >
      {j.status !== 'completed' ? (
        <CircleDashed size={14} color="var(--ops-run)" />
      ) : j.conclusion === 'success' || j.conclusion === 'neutral' || j.conclusion === 'skipped' ? (
        <CheckCircle2 size={14} color="var(--ops-ok)" />
      ) : (
        <XCircle size={14} color="var(--ops-bad)" />
      )}
      <span className="ops-ci__name">{j.name}</span>
      <span>{j.startedAt && j.completedAt ? minutes(j.startedAt, j.completedAt) : j.status !== 'completed' ? j.status.replace('_', ' ') : (j.conclusion ?? '')}</span>
    </button>
  );
}
